import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, Users, Clock, CheckCircle2, AlertCircle,
  Mail, Phone, Building2, MapPin, Calendar, Search, Filter,
  Eye, Edit, Loader2, Lock, LogOut, RefreshCw, FileText,
  TrendingUp, XCircle, PlayCircle
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Dev-only error logger (no console output in production builds)
const devError = (...args) => {
  if (process.env.NODE_ENV !== 'production') console.error(...args);
};

// Auth token storage keys. sessionStorage clears on browser close and has a
// much smaller XSS window than localStorage. A full httpOnly-cookie flow would
// require backend session cookies; sessionStorage is a pragmatic middle-ground.
const TOKEN_KEY = 'cafetrix_admin_token';
const EMAIL_KEY = 'cafetrix_admin_email';

const CafetrixAdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  const [submissions, setSubmissions] = useState([]);
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    in_progress: 0,
    scheduled: 0,
    completed: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [updateStatus, setUpdateStatus] = useState('');
  const [updateNotes, setUpdateNotes] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // authFetch — always injects the Bearer token; on 401 wipes session and
  // signs the user out so the login screen re-renders.
  const authFetch = useCallback(async (url, options = {}) => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    const headers = { ...(options.headers || {}) };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(url, { ...options, headers });
    if (res.status === 401) {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(EMAIL_KEY);
      setIsAuthenticated(false);
      setAdminEmail('');
    }
    return res;
  }, []);

  const statusColors = {
    pending: { bg: 'rgba(255, 193, 7, 0.1)', border: '#ffc107', text: '#ffc107' },
    in_progress: { bg: 'rgba(0, 212, 255, 0.1)', border: '#00D4FF', text: '#00D4FF' },
    scheduled: { bg: 'rgba(107, 203, 119, 0.1)', border: '#6BCB77', text: '#6BCB77' },
    completed: { bg: 'rgba(39, 174, 96, 0.1)', border: '#27ae60', text: '#27ae60' },
    cancelled: { bg: 'rgba(231, 76, 60, 0.1)', border: '#e74c3c', text: '#e74c3c' }
  };

  const statusIcons = {
    pending: <Clock size={16} />,
    in_progress: <PlayCircle size={16} />,
    scheduled: <Calendar size={16} />,
    completed: <CheckCircle2 size={16} />,
    cancelled: <XCircle size={16} />
  };

  // On mount: if a token exists, validate it via /admin/me before trusting it.
  useEffect(() => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cafetrix/admin/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setAdminEmail(data.email || sessionStorage.getItem(EMAIL_KEY) || '');
          setIsAuthenticated(true);
        } else {
          sessionStorage.removeItem(TOKEN_KEY);
          sessionStorage.removeItem(EMAIL_KEY);
        }
      } catch (e) {
        devError('Session validation failed:', e);
      }
    })();
  }, []);

  const fetchSubmissions = useCallback(async () => {
    setIsLoading(true);
    try {
      const url = filterStatus 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/cafetrix/admin/submissions?status=${filterStatus}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/cafetrix/admin/submissions`;
      const response = await authFetch(url);
      if (!response.ok) return;
      const data = await response.json();
      setSubmissions(data.submissions || []);
      setStatistics(data.statistics || {
        total: 0, pending: 0, in_progress: 0, scheduled: 0, completed: 0
      });
    } catch (error) {
      devError('Error fetching submissions:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filterStatus, authFetch]);

  useEffect(() => {
    if (isAuthenticated) fetchSubmissions();
  }, [isAuthenticated, fetchSubmissions]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoggingIn(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cafetrix/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });

      const data = await response.json();

      if (data.success && data.token) {
        sessionStorage.setItem(TOKEN_KEY, data.token);
        sessionStorage.setItem(EMAIL_KEY, data.email || loginEmail);
        setAdminEmail(data.email || loginEmail);
        setIsAuthenticated(true);
      } else {
        setLoginError(data.message || 'Access denied');
      }
    } catch (error) {
      devError('Login error:', error);
      setLoginError('Unable to verify access. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAdminEmail('');
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(EMAIL_KEY);
    setSubmissions([]);
    setSelectedSubmission(null);
  };

  const handleUpdateStatus = async () => {
    if (!selectedSubmission || !updateStatus) return;
    
    setIsUpdating(true);
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cafetrix/admin/submission/${selectedSubmission.ticket_number}/status`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: updateStatus, notes: updateNotes })
        }
      );

      if (response.ok) {
        await fetchSubmissions();
        // Update selected submission
        const updated = await authFetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/cafetrix/admin/submission/${selectedSubmission.ticket_number}`
        );
        if (updated.ok) {
          const updatedData = await updated.json();
          setSelectedSubmission(updatedData);
        }
        setUpdateStatus('');
        setUpdateNotes('');
      }
    } catch (error) {
      devError('Error updating status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const filteredSubmissions = submissions.filter(sub => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      sub.ticket_number?.toLowerCase().includes(search) ||
      sub.fullName?.toLowerCase().includes(search) ||
      sub.company?.toLowerCase().includes(search) ||
      sub.email?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Page
  if (!isAuthenticated) {
    return (
      <div style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
        <Header />
        <main style={{ paddingTop: '180px' }}>
          <section style={{ padding: '100px 7.6923%' }}>
            <div style={{ maxWidth: '450px', margin: '0 auto' }}>
              <div style={{
                padding: '50px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)'
              }}>
                <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1a1f71 0%, #2d3494 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px'
                  }}>
                    <Lock size={36} color="#00D4FF" />
                  </div>
                  <h1 className="heading-1" style={{ marginBottom: '12px' }}>
                    Admin Dashboard
                  </h1>
                  <p className="body-medium" style={{ color: 'var(--text-muted)' }}>
                    CafetriX Labs Request Management
                  </p>
                </div>

                <form onSubmit={handleLogin} data-testid="admin-login-form">
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      VDM Email Address
                    </label>
                    <input
                      data-testid="admin-login-email"
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="your.name@vdmbi.org"
                      required
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: loginError ? '2px solid #FF6B6B' : '1px solid var(--border-subtle)',
                        color: 'var(--text-primary)',
                        fontSize: '16px',
                        outline: 'none'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Password
                    </label>
                    <input
                      data-testid="admin-login-password"
                      type="password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: loginError ? '2px solid #FF6B6B' : '1px solid var(--border-subtle)',
                        color: 'var(--text-primary)',
                        fontSize: '16px',
                        outline: 'none'
                      }}
                    />
                    {loginError && (
                      <p style={{ color: '#FF6B6B', fontSize: '14px', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <AlertCircle size={16} />
                        {loginError}
                      </p>
                    )}
                  </div>

                  <button
                    data-testid="admin-login-submit"
                    type="submit"
                    disabled={isLoggingIn}
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: '#00D4FF',
                      color: '#ffffff',
                      fontWeight: '600',
                      fontSize: '16px',
                      border: 'none',
                      cursor: isLoggingIn ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px'
                    }}
                  >
                    {isLoggingIn ? (
                      <>
                        <Loader2 size={20} className="animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        Access Dashboard
                        <ArrowRight size={20} />
                      </>
                    )}
                  </button>
                </form>

                <div style={{
                  marginTop: '30px',
                  padding: '20px',
                  background: 'rgba(255, 107, 107, 0.05)',
                  border: '1px solid rgba(255, 107, 107, 0.3)'
                }}>
                  <p className="body-small" style={{ color: 'var(--text-muted)', textAlign: 'center' }}>
                    🔒 Access restricted to @vdmbi.org email addresses only
                  </p>
                </div>
              </div>

              <div style={{ textAlign: 'center', marginTop: '30px' }}>
                <Link to="/cafetrix-labs" style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: 'var(--text-muted)',
                  textDecoration: 'none',
                  fontSize: '14px'
                }}>
                  <ArrowLeft size={16} />
                  Back to CafetriX Labs
                </Link>
              </div>
            </div>
          </section>
        </main>
        <Footer />

        <style>{`
          .animate-spin {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Dashboard Page
  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
      <Header />
      <main style={{ paddingTop: '180px' }}>
        {/* Dashboard Header */}
        <section style={{
          background: 'linear-gradient(135deg, #1a1f71 0%, #2d3494 100%)',
          padding: '40px 7.6923%'
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_vdmbusiness/artifacts/v6hrcwjd_ChatGPT%20Image%20Jan%201%2C%202026%2C%2009_07_53%20PM.png" 
                    alt="CafetriX Labs" 
                    style={{ height: '40px', objectFit: 'contain' }}
                  />
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '24px' }}>|</span>
                  <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#ffffff' }}>
                    Admin Dashboard
                  </h1>
                </div>
                <p data-testid="admin-logged-in-as" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
                  Logged in as: {adminEmail || sessionStorage.getItem(EMAIL_KEY) || ''}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={fetchSubmissions}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  <RefreshCw size={16} />
                  Refresh
                </button>
                <button
                  data-testid="admin-logout-button"
                  onClick={handleLogout}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    background: 'rgba(255, 107, 107, 0.2)',
                    border: '1px solid #FF6B6B',
                    color: '#FF6B6B',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Statistics Cards */}
        <section style={{ padding: '40px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '20px'
            }} className="stats-grid">
              <div style={{
                padding: '24px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid var(--border-subtle)'
              }}>
                <TrendingUp size={24} color="#00D4FF" style={{ marginBottom: '12px' }} />
                <p className="body-small" style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>Total Requests</p>
                <p data-testid="admin-stat-total" style={{ fontSize: '32px', fontWeight: '700', color: '#00D4FF' }}>{statistics.total}</p>
              </div>
              <div style={{
                padding: '24px',
                background: statusColors.pending.bg,
                border: `1px solid ${statusColors.pending.border}`
              }}>
                <Clock size={24} color={statusColors.pending.text} style={{ marginBottom: '12px' }} />
                <p className="body-small" style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>Pending</p>
                <p style={{ fontSize: '32px', fontWeight: '700', color: statusColors.pending.text }}>{statistics.pending}</p>
              </div>
              <div style={{
                padding: '24px',
                background: statusColors.in_progress.bg,
                border: `1px solid ${statusColors.in_progress.border}`
              }}>
                <PlayCircle size={24} color={statusColors.in_progress.text} style={{ marginBottom: '12px' }} />
                <p className="body-small" style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>In Progress</p>
                <p style={{ fontSize: '32px', fontWeight: '700', color: statusColors.in_progress.text }}>{statistics.in_progress}</p>
              </div>
              <div style={{
                padding: '24px',
                background: statusColors.scheduled.bg,
                border: `1px solid ${statusColors.scheduled.border}`
              }}>
                <Calendar size={24} color={statusColors.scheduled.text} style={{ marginBottom: '12px' }} />
                <p className="body-small" style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>Scheduled</p>
                <p style={{ fontSize: '32px', fontWeight: '700', color: statusColors.scheduled.text }}>{statistics.scheduled}</p>
              </div>
              <div style={{
                padding: '24px',
                background: statusColors.completed.bg,
                border: `1px solid ${statusColors.completed.border}`
              }}>
                <CheckCircle2 size={24} color={statusColors.completed.text} style={{ marginBottom: '12px' }} />
                <p className="body-small" style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>Completed</p>
                <p style={{ fontSize: '32px', fontWeight: '700', color: statusColors.completed.text }}>{statistics.completed}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section style={{ padding: '0 7.6923% 80px' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: selectedSubmission ? '1fr 500px' : '1fr',
              gap: '30px'
            }} className="dashboard-layout">
              {/* Submissions List */}
              <div>
                {/* Filters */}
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  marginBottom: '24px',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ flex: 1, minWidth: '250px' }}>
                    <div style={{ position: 'relative' }}>
                      <Search size={18} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                      <input
                        type="text"
                        placeholder="Search by ticket, name, company, or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px 14px 12px 44px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid var(--border-subtle)',
                          color: 'var(--text-primary)',
                          fontSize: '14px',
                          outline: 'none'
                        }}
                      />
                    </div>
                  </div>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    style={{
                      padding: '12px 16px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid var(--border-subtle)',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      outline: 'none',
                      minWidth: '150px'
                    }}
                  >
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Submissions Table */}
                <div style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid var(--border-subtle)',
                  overflow: 'hidden'
                }}>
                  {isLoading ? (
                    <div style={{ padding: '60px', textAlign: 'center' }}>
                      <Loader2 size={40} color="#00D4FF" className="animate-spin" />
                      <p className="body-medium" style={{ marginTop: '16px', color: 'var(--text-muted)' }}>Loading submissions...</p>
                    </div>
                  ) : filteredSubmissions.length === 0 ? (
                    <div style={{ padding: '60px', textAlign: 'center' }}>
                      <FileText size={48} color="var(--text-muted)" style={{ marginBottom: '16px' }} />
                      <p className="body-medium" style={{ color: 'var(--text-muted)' }}>No submissions found</p>
                    </div>
                  ) : (
                    <table data-testid="admin-submissions-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: 'rgba(0, 212, 255, 0.1)' }}>
                          <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#00D4FF' }}>Ticket</th>
                          <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#00D4FF' }}>Company</th>
                          <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#00D4FF' }}>Contact</th>
                          <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#00D4FF' }}>Status</th>
                          <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#00D4FF' }}>Date</th>
                          <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', fontSize: '13px', color: '#00D4FF' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSubmissions.map((sub, index) => (
                          <tr 
                            key={sub.id || index}
                            style={{
                              borderBottom: '1px solid var(--border-subtle)',
                              background: selectedSubmission?.ticket_number === sub.ticket_number ? 'rgba(0, 212, 255, 0.05)' : 'transparent',
                              cursor: 'pointer',
                              transition: 'background 0.2s'
                            }}
                            onClick={() => setSelectedSubmission(sub)}
                          >
                            <td style={{ padding: '14px 16px' }}>
                              <span style={{ fontWeight: '600', color: '#00D4FF', fontFamily: 'monospace' }}>
                                {sub.ticket_number}
                              </span>
                            </td>
                            <td style={{ padding: '14px 16px' }}>
                              <p className="body-small" style={{ fontWeight: '500' }}>{sub.company}</p>
                              <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{sub.location}</p>
                            </td>
                            <td style={{ padding: '14px 16px' }}>
                              <p className="body-small" style={{ fontWeight: '500' }}>{sub.fullName}</p>
                              <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{sub.email}</p>
                            </td>
                            <td style={{ padding: '14px 16px' }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 12px',
                                background: statusColors[sub.status]?.bg || statusColors.pending.bg,
                                border: `1px solid ${statusColors[sub.status]?.border || statusColors.pending.border}`,
                                color: statusColors[sub.status]?.text || statusColors.pending.text,
                                fontSize: '12px',
                                fontWeight: '500',
                                textTransform: 'capitalize'
                              }}>
                                {statusIcons[sub.status]}
                                {sub.status?.replace('_', ' ')}
                              </span>
                            </td>
                            <td style={{ padding: '14px 16px' }}>
                              <p className="body-small">{formatDate(sub.submission_time)}</p>
                            </td>
                            <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedSubmission(sub);
                                }}
                                style={{
                                  padding: '6px 12px',
                                  background: 'rgba(0, 212, 255, 0.1)',
                                  border: '1px solid #00D4FF',
                                  color: '#00D4FF',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  fontSize: '12px',
                                  margin: '0 auto'
                                }}
                              >
                                <Eye size={14} />
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Detail Panel */}
              {selectedSubmission && (
                <div style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid var(--border-subtle)',
                  padding: '30px',
                  maxHeight: 'calc(100vh - 300px)',
                  overflowY: 'auto',
                  position: 'sticky',
                  top: '200px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h3 className="heading-2" style={{ color: '#00D4FF' }}>
                      {selectedSubmission.ticket_number}
                    </h3>
                    <button
                      onClick={() => setSelectedSubmission(null)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--text-muted)',
                        cursor: 'pointer',
                        fontSize: '20px'
                      }}
                    >
                      ×
                    </button>
                  </div>

                  {/* Current Status */}
                  <div style={{ marginBottom: '24px' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      background: statusColors[selectedSubmission.status]?.bg,
                      border: `1px solid ${statusColors[selectedSubmission.status]?.border}`,
                      color: statusColors[selectedSubmission.status]?.text,
                      fontSize: '14px',
                      fontWeight: '500',
                      textTransform: 'capitalize'
                    }}>
                      {statusIcons[selectedSubmission.status]}
                      {selectedSubmission.status?.replace('_', ' ')}
                    </span>
                  </div>

                  {/* Contact Info */}
                  <div style={{ marginBottom: '24px' }}>
                    <h4 className="heading-3" style={{ marginBottom: '12px' }}>Contact Information</h4>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      <p className="body-small"><strong>Name:</strong> {selectedSubmission.fullName}</p>
                      <p className="body-small"><strong>Email:</strong> <a href={`mailto:${selectedSubmission.email}`} style={{ color: '#00D4FF' }}>{selectedSubmission.email}</a></p>
                      <p className="body-small"><strong>Phone:</strong> <a href={`tel:${selectedSubmission.phone}`} style={{ color: '#00D4FF' }}>{selectedSubmission.phone}</a></p>
                      <p className="body-small"><strong>Company:</strong> {selectedSubmission.company}</p>
                      <p className="body-small"><strong>Job Title:</strong> {selectedSubmission.jobTitle}</p>
                    </div>
                  </div>

                  {/* Organization */}
                  <div style={{ marginBottom: '24px' }}>
                    <h4 className="heading-3" style={{ marginBottom: '12px' }}>Organization Details</h4>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      <p className="body-small"><strong>Type:</strong> {selectedSubmission.organizationType}</p>
                      <p className="body-small"><strong>Employees:</strong> {selectedSubmission.employeeCount}</p>
                      <p className="body-small"><strong>Location:</strong> {selectedSubmission.location}</p>
                      <p className="body-small"><strong>Cafeterias:</strong> {selectedSubmission.cafeteriaCount || '-'}</p>
                    </div>
                  </div>

                  {/* Pain Points */}
                  {selectedSubmission.painPoints?.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <h4 className="heading-3" style={{ marginBottom: '12px' }}>Pain Points</h4>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {selectedSubmission.painPoints.map((point, idx) => (
                          <span key={idx} style={{
                            padding: '4px 10px',
                            background: 'rgba(255, 107, 107, 0.1)',
                            border: '1px solid #FF6B6B',
                            color: '#FF6B6B',
                            fontSize: '12px'
                          }}>
                            {point}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Specific Concerns */}
                  {selectedSubmission.specificConcerns && (
                    <div style={{ marginBottom: '24px' }}>
                      <h4 className="heading-3" style={{ marginBottom: '12px' }}>Specific Concerns</h4>
                      <p className="body-small" style={{ 
                        padding: '12px', 
                        background: 'rgba(255, 255, 255, 0.05)', 
                        border: '1px solid var(--border-subtle)',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {selectedSubmission.specificConcerns}
                      </p>
                    </div>
                  )}

                  {/* Meeting Preferences */}
                  <div style={{ marginBottom: '24px' }}>
                    <h4 className="heading-3" style={{ marginBottom: '12px' }}>Meeting Preferences</h4>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      <p className="body-small"><strong>Format:</strong> {selectedSubmission.meetingFormat || '-'}</p>
                      <p className="body-small"><strong>Available From:</strong> {selectedSubmission.availableFrom || '-'}</p>
                      <p className="body-small"><strong>Time Slots:</strong> {selectedSubmission.preferredTimeSlots?.join(', ') || '-'}</p>
                    </div>
                  </div>

                  {/* Status History */}
                  {selectedSubmission.status_history?.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <h4 className="heading-3" style={{ marginBottom: '12px' }}>Status History</h4>
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {selectedSubmission.status_history.map((history, idx) => (
                          <div key={idx} style={{
                            padding: '10px 12px',
                            background: 'rgba(255, 255, 255, 0.03)',
                            borderLeft: `3px solid ${statusColors[history.status]?.border || '#666'}`,
                            fontSize: '13px'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontWeight: '500', textTransform: 'capitalize' }}>{history.status?.replace('_', ' ')}</span>
                              <span style={{ color: 'var(--text-muted)' }}>{formatDate(history.timestamp)}</span>
                            </div>
                            {history.notes && <p style={{ color: 'var(--text-muted)', marginTop: '4px' }}>{history.notes}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Update Status */}
                  <div style={{
                    padding: '20px',
                    background: 'rgba(0, 212, 255, 0.05)',
                    border: '1px solid #00D4FF'
                  }}>
                    <h4 className="heading-3" style={{ marginBottom: '16px', color: '#00D4FF' }}>Update Status</h4>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      <select
                        value={updateStatus}
                        onChange={(e) => setUpdateStatus(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid var(--border-subtle)',
                          color: 'var(--text-primary)',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">Select new status</option>
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      <textarea
                        placeholder="Add notes (optional)"
                        value={updateNotes}
                        onChange={(e) => setUpdateNotes(e.target.value)}
                        rows={2}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid var(--border-subtle)',
                          color: 'var(--text-primary)',
                          fontSize: '14px',
                          resize: 'vertical'
                        }}
                      />
                      <button
                        onClick={handleUpdateStatus}
                        disabled={!updateStatus || isUpdating}
                        style={{
                          padding: '12px',
                          background: updateStatus ? '#00D4FF' : '#666',
                          color: '#ffffff',
                          fontWeight: '600',
                          fontSize: '14px',
                          border: 'none',
                          cursor: updateStatus ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        {isUpdating ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            Updating...
                          </>
                        ) : (
                          <>
                            <Edit size={16} />
                            Update Status
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <style>{`
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 1200px) {
          .dashboard-layout {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default CafetrixAdminDashboard;
